import React, { useEffect, createElement, Component } from 'react';
/**
 * Runtime SEO injection — sets document title, meta tags, Open Graph / Twitter cards,
 * a favicon (SVG data URI), preconnect hints for faster image loading, and a
 * LocalBusiness JSON-LD schema for rich results.
 *
 * Note: For a production-grade favicon shipped in index.html, upload one via the
 * Publish flow (top-right of Magic Patterns). This component injects a fallback
 * favicon at runtime so the browser tab still shows our branded mark.
 */
export function SEO() {
  useEffect(() => {
    const title = 'Sestri za tačke — Sprehajanje psov v okolici Krškega';
    const description = 'Sestri Eli in Ema iz okolice Krškega z veseljem poskrbiva za sprehod, igro in osnovno skrb za vašega kužka. Pošteni cenik, paket 10 sprehodov s popustom. Pokliči 040 335 549.';
    const url = typeof window !== 'undefined' ? window.location.href : '';
    document.title = title;
    document.documentElement.lang = 'sl';
    const head = document.head;
    const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const upsertLink = (rel: string, href: string, attrs: Record<string, string> = {}) => {
      let el = head.querySelector(`link[rel="${rel}"]${attrs.type ? `[type="${attrs.type}"]` : ''}`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        head.appendChild(el);
      }
      el.setAttribute('href', href);
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };
    // Primary meta
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', 'sprehajanje psov Krško, pasja varuška Krško, dog walking Krško, sprehajanje psov, okolica Krškega, Slovenija, hranjenje hišnih ljubljenčkov, pet sitting, Sestri za tačke');
    upsertMeta('name', 'author', 'Eli in Ema · Sestri za tačke');
    upsertMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover');
    upsertMeta('name', 'theme-color', '#C9A789');
    upsertMeta('name', 'robots', 'index, follow');
    // Open Graph
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:locale', 'sl_SI');
    upsertMeta('property', 'og:site_name', 'Sestri za tačke');
    if (url) upsertMeta('property', 'og:url', url);
    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    // Canonical
    if (url) upsertLink('canonical', url);
    // Favicon — embed our brand mark as an inline SVG data URI.
    const faviconSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <rect x="2" y="2" width="60" height="60" rx="18" fill="#C9A789"/>
  <path d="M32 41c-7-4.2-11-8.4-11-13.2 0-3 2.4-5.4 5.4-5.4 2.1 0 3.9 1.2 4.8 3 .9-1.8 2.7-3 4.8-3 3 0 5.4 2.4 5.4 5.4 0 4.8-4 9-9.4 13.2Z" fill="#F8C8C8"/>
  <g transform="translate(10 18) rotate(-15)" fill="#B8DDB8">
    <circle cx="6" cy="6" r="3.4"/>
    <circle cx="11.5" cy="2.5" r="2.4"/>
    <circle cx="0.5" cy="2.5" r="2.4"/>
    <circle cx="3" cy="-2" r="2"/>
    <circle cx="9" cy="-2" r="2"/>
  </g>
  <g transform="translate(48 44) rotate(15)" fill="#FFFBF5">
    <circle cx="0" cy="0" r="3.8"/>
    <circle cx="-6" cy="-3.5" r="2.6"/>
    <circle cx="5.5" cy="-3.5" r="2.6"/>
    <circle cx="-3" cy="-8" r="2.2"/>
    <circle cx="3.5" cy="-8" r="2.2"/>
  </g>
</svg>`;
    const faviconHref = `data:image/svg+xml;utf8,${encodeURIComponent(faviconSvg)}`;
    upsertLink('icon', faviconHref, {
      type: 'image/svg+xml'
    });
    upsertLink('apple-touch-icon', faviconHref);
    // Performance hints — preconnect to image hosts used in the hero / contact QR
    const preconnects = ['https://images.unsplash.com', 'https://cdn.magicpatterns.com', 'https://api.qrserver.com', 'https://fonts.gstatic.com'];
    preconnects.forEach(href => {
      if (!head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'preconnect');
        link.setAttribute('href', href);
        link.setAttribute('crossorigin', '');
        head.appendChild(link);
      }
    });
    // Preload the hero image so it shows up instantly on QR-scan arrivals
    const heroSrc = 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=2000&q=80';
    if (!head.querySelector(`link[rel="preload"][href="${heroSrc}"]`)) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('href', heroSrc);
      link.setAttribute('fetchpriority', 'high');
      head.appendChild(link);
    }
    // LocalBusiness structured data for rich results
    const ldId = 'sestri-za-tacke-ld-json';
    let ldEl = document.getElementById(ldId) as HTMLScriptElement | null;
    if (!ldEl) {
      ldEl = document.createElement('script');
      ldEl.id = ldId;
      ldEl.type = 'application/ld+json';
      head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Sestri za tačke',
      description,
      areaServed: {
        '@type': 'City',
        name: 'Krško',
        addressCountry: 'SI'
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Krško',
        addressCountry: 'SI'
      },
      inLanguage: 'sl',
      url,
      email: 'sestrizatacke@siter.si',
      telephone: ['+38640335549', '+38640334347'],
      priceRange: '€€',
      makesOffer: [{
        '@type': 'Offer',
        name: 'Kratek sprehod (do 30 min)',
        price: '10',
        priceCurrency: 'EUR'
      }, {
        '@type': 'Offer',
        name: 'Dolg sprehod (do 90 min)',
        price: '15',
        priceCurrency: 'EUR'
      }, {
        '@type': 'Offer',
        name: 'Paket 10 sprehodov (15% popust)',
        priceCurrency: 'EUR'
      }]
    });
  }, []);
  return null;
}